(() => {
  const CARD_SELECTOR = '[data-lunalea-product-card]';
  const DESKTOP_HOVER_QUERY = '(hover: hover) and (pointer: fine)';

  class LunaleaProductCard {
    constructor(card) {
      this.card = card;
      this.track = card.querySelector('[data-card-track]');
      this.media = card.querySelector('[data-card-media]');
      this.link = card.querySelector('[data-card-media-link]');
      this.slides = Array.from(card.querySelectorAll('[data-card-slide]'));
      this.dots = Array.from(card.querySelectorAll('[data-card-dot]'));
      this.swatches = Array.from(card.querySelectorAll('[data-card-swatch]'));
      this.variantInput = card.querySelector('[data-card-variant-input]');
      this.currentPrice = card.querySelector('[data-card-current-price]');
      this.comparePrice = card.querySelector('[data-card-compare-price]');
      this.quickAdd = card.querySelector('quick-add');
      this.index = 0;
      this.dragging = false;
      this.dragged = false;
      this.hoverPreviewEnabled = window.matchMedia(DESKTOP_HOVER_QUERY).matches;
      this.startX = 0;
      this.startY = 0;
      this.deltaX = 0;
      this.pointerId = null;

      if (this.track && this.slides.length > 1) {
        this.bindSlider();
        this.updatePagination();
      }
      if (this.swatches.length) this.bindSwatches();
    }

    bindSlider() {
      this.link?.addEventListener('dragstart', (event) => event.preventDefault());
      this.link?.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.link?.addEventListener('pointermove', (event) => this.onPointerMove(event), { passive: false });
      this.link?.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.link?.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.link?.addEventListener('pointerleave', (event) => this.onPointerLeave(event));

      this.link?.addEventListener('click', (event) => {
        if (this.dragged) {
          event.preventDefault();
          this.dragged = false;
        }
      });

      if (this.hoverPreviewEnabled) {
        this.media?.addEventListener('mouseenter', () => {
          if (this.dragging || this.slides.length < 2) return;
          this.goTo(1);
        });

        this.media?.addEventListener('mouseleave', () => {
          if (this.dragging) return;
          this.goTo(0);
        });
      }
    }

    onPointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;
      this.dragging = true;
      this.dragged = false;
      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.deltaX = 0;
      this.track.classList.add('is-dragging');
      this.link?.classList.add('is-dragging');
      try { this.link?.setPointerCapture?.(event.pointerId); } catch (_) {}
    }

    onPointerMove(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;

      if (Math.abs(deltaX) > 6 && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.dragged = true;
        this.deltaX = deltaX;
        const width = this.media?.clientWidth || 1;
        const percent = (deltaX / width) * 100;
        this.track.style.transform = `translate3d(${(-this.index * 100) + percent}%, 0, 0)`;
        if (event.cancelable) event.preventDefault();
      }
    }

    onPointerUp(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      this.dragging = false;
      this.track.classList.remove('is-dragging');
      this.link?.classList.remove('is-dragging');

      const threshold = Math.min(55, (this.media?.clientWidth || 300) * 0.14);
      if (this.deltaX <= -threshold) this.goTo(this.index + 1);
      else if (this.deltaX >= threshold) this.goTo(this.index - 1);
      else this.goTo(this.index);

      this.deltaX = 0;
      try { this.link?.releasePointerCapture?.(event.pointerId); } catch (_) {}
      this.pointerId = null;
    }

    onPointerLeave(event) {
      if (!this.dragging || event.pointerId !== this.pointerId) return;
      this.onPointerUp(event);
    }

    goTo(index) {
      if (!this.track || !this.slides.length) return;
      const count = this.slides.length;
      this.index = Math.max(0, Math.min(index, count - 1));
      this.track.style.transform = `translate3d(-${this.index * 100}%, 0, 0)`;
      this.updatePagination();
    }

    updatePagination() {
      if (!this.dots.length) return;
      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === this.index);
      });
    }

    bindSwatches() {
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          this.swatches.forEach((item) => {
            const active = item === swatch;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

          const mediaId = swatch.dataset.mediaId;
          let mediaMatched = false;
          if (mediaId) {
            const mediaIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
            if (mediaIndex >= 0) {
              this.goTo(mediaIndex);
              mediaMatched = true;
            }
          }

          if (!mediaMatched && swatch.dataset.imageSrc && this.slides[0]) {
            const firstSlide = this.slides[0];
            const firstImage = firstSlide.querySelector('img');
            if (firstImage) {
              firstImage.src = swatch.dataset.imageSrc;
              firstImage.srcset = swatch.dataset.imageSrc;
            }
            firstSlide.dataset.mediaId = mediaId || '';
            firstSlide.dataset.imageSrc = swatch.dataset.imageSrc;
            this.goTo(0);
          } else if (!mediaMatched) {
            this.goTo(0);
          }

          if (this.variantInput && swatch.dataset.variantId) {
            this.variantInput.value = swatch.dataset.variantId;
          }

          const directAddButton = this.card.querySelector('product-form .ll-product-card__plus');
          if (directAddButton && swatch.dataset.available) {
            directAddButton.disabled = swatch.dataset.available !== 'true';
          }

          if (this.currentPrice && swatch.dataset.price) {
            this.currentPrice.textContent = swatch.dataset.price;
          }

          if (swatch.dataset.comparePrice) {
            if (!this.comparePrice) {
              this.comparePrice = document.createElement('span');
              this.comparePrice.className = 'll-product-card__price-old';
              this.comparePrice.dataset.cardComparePrice = '';
              this.currentPrice?.before(this.comparePrice);
            }
            this.comparePrice.textContent = swatch.dataset.comparePrice;
            this.comparePrice.hidden = false;
          } else if (this.comparePrice) {
            this.comparePrice.hidden = true;
          }

          if (this.quickAdd && swatch.dataset.variantUrl) {
            this.quickAdd.product_url = swatch.dataset.variantUrl;
            const quickAddButton = this.quickAdd.querySelector('button[data-product-url]');
            quickAddButton?.setAttribute('data-product-url', swatch.dataset.variantUrl);
          }
        });
      });
    }
  }

  const init = (root = document) => {
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (card.dataset.lunaleaCardReady === 'true') return;
      card.dataset.lunaleaCardReady = 'true';
      new LunaleaProductCard(card);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.(CARD_SELECTOR)) init(node.parentElement || document);
        else if (node.querySelector?.(CARD_SELECTOR)) init(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
